"use client";

import Link from "next/link";
import { Media } from "payload-types";

export type CardGridBlockProps = {
  blockType: "cardGrid";
  title?: string;
  columns: string;
  cards: CardProps[];
};

type CardProps = {
  description: string;
  source?: string;
  sourceLink?: string;
  image?: number | Media;
  backgroundColor?: "white" | "orange" | "yellow" | "blue" | "purple";
  size?: "normal" | "wide" | "large";
};

type Props = CardGridBlockProps & {
  className?: string;
};

export const CardGrid: React.FC<Props> = ({
  className,
  title,
  columns = "3",
  cards = [],
}: Props) => {
  const cardItems = Array.isArray(cards) ? cards : [];

  const handleTweet = (text: string) => {
    const tweetText = encodeURIComponent(`${text} - Source - ${window.location.href}`);
    window.open(`https://x.com/intent/tweet?text=${tweetText}`, "_blank");
  };

  const handleCopy = async (text: string) => {
    if (!text) return;

    text = text + " via Magic Hour AI, Inc.";

    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  const getColor = (backgroundColor?: string) => {
    switch (backgroundColor) {
      case "white":
        return { backgroundClass: "bg-white", textColor: "text-black", fillColor: "#000000" };
      case "orange":
        return { backgroundClass: "bg-orange-800", textColor: "text-white", fillColor: "#FFFFFF" };
      case "yellow":
        return { backgroundClass: "bg-yellow-500", textColor: "text-black", fillColor: "#000000" };
      case "blue":
        return { backgroundClass: "bg-blue-500", textColor: "text-white", fillColor: "#FFFFFF" };
      case "purple":
        return { backgroundClass: "bg-purple-500", textColor: "text-white", fillColor: "#FFFFFF" };
      default:
        return {
          backgroundClass: "bg-[rgb(30, 28, 44)]",
          textColor: "text-white",
          fillColor: "rgb(84, 100, 208)",
        };
    }
  };

  const getGridColsClass = (): string => {
    switch (columns) {
      case "2":
        return "grid-cols-1 md:grid-cols-2";
      case "3":
        return "grid-cols-1 md:grid-cols-2 lg:grid-cols-3";
      case "4":
        return "grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4";
      default:
        return "grid-cols-1 md:grid-cols-3";
    }
  };

  if (cardItems.length === 0) {
    return (
      <div className="rounded-lg bg-[rgb(30,28,44)] p-8 text-center text-white">
        <p>Add cards to display a grid of information</p>
      </div>
    );
  }

  const buttonClass =
    "bg-transparent p-1.5 rounded opacity-70 hover:opacity-100 transition-all md:min-w-[36px] md:min-h-[36px] md:flex md:items-center md:justify-center";

  return (
    <section className={`my-8 w-full ${className || ""}`}>
      {title && <h2 className="mb-6 text-center text-2xl font-semibold text-white">{title}</h2>}

      <div className={`grid w-full gap-4 ${getGridColsClass()} auto-rows-auto`}>
        {cardItems.map((card, index) => {
          const sizeClass = card.size === "wide" || card.size === "large" ? "md:col-span-2" : "";
          const { backgroundClass, textColor, fillColor } = getColor(card.backgroundColor);

          return (
            <div
              key={`card-${index}`}
              className={`relative flex h-full flex-col overflow-hidden rounded-lg border border-solid border-[rgb(76,76,76)] bg-[rgb(30,28,44)] p-0 text-sm break-words shadow-md transition-all duration-200 hover:shadow-lg ${textColor} hover:border-blue-500 ${sizeClass} group ${backgroundClass}`}
            >
              {card.image && (
                <div className="w-full overflow-hidden leading-none">
                  {typeof card.image !== "number" && card.image.url && (
                    <img
                      src={card.image.url}
                      alt={card.image.alt || "Card image"}
                      className="max-h-[180px] w-full object-cover"
                    />
                  )}
                </div>
              )}
              <div className="flex flex-grow flex-col p-4">
                <div className="relative z-10 mb-2 flex-grow leading-6">{card.description}</div>
                {card.source && card.sourceLink && (
                  <Link
                    href={card.sourceLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`text-xs opacity-75 ${textColor} relative z-10 mb-2 no-underline`}
                  >
                    Source: {card.source}
                  </Link>
                )}
                <div className="relative z-10 mt-2 flex justify-end gap-2">
                  <button
                    className={`${buttonClass} ${backgroundClass} ${textColor}`}
                    title="Share on X"
                    onClick={() => handleTweet(card.description)}
                  >
                    <svg
                      className="transition-transform hover:scale-110 md:h-[18px] md:w-[18px]"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill={fillColor}
                      stroke="none"
                    >
                      <path d="M22.46 6c-.77.35-1.6.58-2.46.69.88-.53 1.56-1.37 1.88-2.38-.83.5-1.75.85-2.72 1.05C18.37 4.5 17.26 4 16 4c-2.35 0-4.27 1.92-4.27 4.29 0 .34.04.67.11.98C8.28 9.09 5.11 7.38 3 4.79c-.37.63-.58 1.37-.58 2.15 0 1.49.75 2.81 1.91 3.56-.71 0-1.37-.2-1.95-.5v.03c0 2.08 1.48 3.82 3.44 4.21a4.22 4.22 0 0 1-1.93.07a4.28 4.28 0 0 0 4 2.98a8.521 8.521 0 0 1-5.33 1.84c-.34 0-.68-.02-1.02-.06C3.44 20.29 5.7 21 8.12 21 16 21 20.33 14.46 20.33 8.79c0-.19 0-.37-.01-.56.84-.6 1.56-1.36 2.14-2.23z" />
                    </svg>
                  </button>
                  <button
                    className={`${buttonClass} ${backgroundClass} ${textColor}`}
                    title="Copy"
                    onClick={() => handleCopy(card.description)}
                  >
                    <svg
                      className="transition-transform hover:scale-110 md:h-[18px] md:w-[18px]"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill={fillColor}
                      stroke="none"
                    >
                      <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};
